import React, { Component } from 'react'
import { Modal, DatePicker, Button, Row, Col, message } from 'antd'
import { FormattedMessage, injectIntl } from 'react-intl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'

import TimezoneSwitch from './TimezoneSwitch'
import { resolveTimezones } from '../utils/date'

// Only 30 days of history are kept in the database
const HISTORY_RETENTION_DAYS = 31

/**
 * Modal to download the "played track" history of a device as a CSV file
 * for a date range. The range is interpreted in the selected timezone
 * (device or local).
 *
 * `exportPath` selects the route to call: devices and third party devices
 * have their own, since they live in different collections.
 */
class DownloadHistory extends Component {
	state = {
		// [start, end] moments, defaults to the last 7 days
		range: [moment().subtract(6, 'days'), moment()],
		useDeviceTimezone: true,
		downloading: false
	}

	handleCancel = () => {
		this.props.onClose()
	}

	download = async () => {
		const { device, exportPath = '/export_history', intl } = this.props
		const { range, useDeviceTimezone } = this.state
		if (!range || !range[0] || !range[1]) {
			return
		}
		const { deviceTimezone, browserTimezone } = resolveTimezones(
			device.timeZone
		)
		const timeZone = useDeviceTimezone ? deviceTimezone : browserTimezone
		// Days are sent as YYYY-MM-DD: the server interprets them as the start
		// of the "from" day and the end of the "to" day in the given timezone
		const from = range[0].format('YYYY-MM-DD')
		const to = range[1].format('YYYY-MM-DD')

		const params = new URLSearchParams({ from, to, timeZone })

		this.setState({ downloading: true })
		try {
			const res = await fetch(
				`${window.location.origin}${exportPath}/${device._id}?${params}`,
				{ credentials: 'include' }
			)
			if (!res.ok) {
				throw new Error(`${res.status} ${res.statusText}`)
			}
			const blob = await res.blob()

			const disposition = res.headers.get('Content-Disposition') || ''
			const match = disposition.match(/filename="([^"]+)"/)
			const filename = match ? match[1] : `history_${from}_${to}.csv`

			const url = window.URL.createObjectURL(blob)
			const link = document.createElement('a')
			link.href = url
			link.download = filename
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			window.URL.revokeObjectURL(url)
		} catch (e) {
			console.error('Error while downloading the history', e)
			message.error(intl.formatMessage({ id: 'history.export.error' }))
		} finally {
			this.setState({ downloading: false })
		}
	}

	render() {
		const { device, intl } = this.props
		const { range, useDeviceTimezone, downloading } = this.state
		const hasRange = !!range && !!range[0] && !!range[1]

		return (
			<Modal
				visible={true}
				title={intl.formatMessage(
					{ id: 'history.export.title' },
					{ name: device.nickname || device.name || '' }
				)}
				onCancel={this.handleCancel}
				footer={[
					<Button key="cancel" onClick={this.handleCancel}>
						<FormattedMessage id="actions.cancel" />
					</Button>,
					<Button
						key="download"
						type="primary"
						onClick={this.download}
						loading={downloading}
						disabled={!hasRange}
						icon={<FontAwesomeIcon icon={faDownload} />}
					>
						&nbsp;
						<FormattedMessage id="history.export.download" />
					</Button>
				]}
			>
				<Row justify="center" align="middle" gutter={[16, 16]}>
					<Col>
						<DatePicker.RangePicker
							format="YYYY-MM-DD"
							allowClear
							disabledDate={(date) =>
								date.isBefore(
									moment()
										.subtract(
											HISTORY_RETENTION_DAYS,
											'days'
										)
										.startOf('day')
								) || date.isAfter(moment().endOf('day'))
							}
							placeholder={[
								intl.formatMessage({
									id: 'history.export.startDate'
								}),
								intl.formatMessage({
									id: 'history.export.endDate'
								})
							]}
							value={range}
							onChange={(range) => this.setState({ range })}
						/>
					</Col>
					<Col>
						<TimezoneSwitch
							deviceTimeZone={device.timeZone}
							checked={useDeviceTimezone}
							onChange={(useDeviceTimezone) =>
								this.setState({ useDeviceTimezone })
							}
						/>
					</Col>
				</Row>
			</Modal>
		)
	}
}

export default injectIntl(DownloadHistory)
